import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, PackageX, AlertTriangle, MapPin, Euro, Filter, ChevronDown, ChevronRight, Search, Check, FileDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { inventoryApi, locationsApi } from '../services/api';
import { usePreferences } from '../contexts/PreferencesContext';

const InventoryReportPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatCurrency } = usePreferences();
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [selectedBatiment, setSelectedBatiment] = useState('');
  const [selectedEtage, setSelectedEtage] = useState('');
  const [selectedBureau, setSelectedBureau] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Location options
  const [batiments, setBatiments] = useState([]);
  const [etages, setEtages] = useState([]);
  const [bureaux, setBureaux] = useState([]);
  
  // Collapsible sections
  const [missingByLocationOpen, setMissingByLocationOpen] = useState({});

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedBatiment) params.batiment_id = selectedBatiment;
      if (selectedEtage) params.etage_id = selectedEtage;
      if (selectedBureau) params.bureau_id = selectedBureau;
      
      const response = await inventoryApi.getReport(id, params);
      setReport(response.data);
    } catch (err) {
      console.error('Erreur chargement rapport:', err);
      setError('Impossible de charger le rapport');
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    if (!report) return;
    
    try {
      // Load batiments for this site
      const batimentsRes = await locationsApi.getByParent(report.site_id);
      setBatiments(batimentsRes.data);
    } catch (err) {
      console.error('Erreur chargement localisations:', err);
    }
  };

  useEffect(() => {
    loadReport();
  }, [id, selectedBatiment, selectedEtage, selectedBureau]);

  useEffect(() => {
    if (report) {
      loadLocations();
    }
  }, [report?.site_id]);

  useEffect(() => {
    if (selectedBatiment) {
      locationsApi.getByParent(selectedBatiment).then(res => {
        setEtages(res.data);
        setSelectedEtage('');
        setSelectedBureau('');
        setBureaux([]);
      });
    } else {
      setEtages([]);
      setSelectedEtage('');
      setSelectedBureau('');
      setBureaux([]);
    }
  }, [selectedBatiment]);

  useEffect(() => {
    if (selectedEtage) {
      locationsApi.getByParent(selectedEtage).then(res => {
        setBureaux(res.data);
        setSelectedBureau('');
      });
    } else {
      setBureaux([]);
      setSelectedBureau('');
    }
  }, [selectedEtage]);

  const filterItems = (items) => {
    if (!searchTerm) return items;
    const term = searchTerm.toLowerCase();
    return items.filter(item => 
      item.code_barres?.toLowerCase().includes(term) ||
      item.marque?.toLowerCase().includes(term) ||
      item.modele?.toLowerCase().includes(term) ||
      item.type_label?.toLowerCase().includes(term) ||
      item.location_path?.toLowerCase().includes(term)
    );
  };

  const handleExportPdf = () => {
    const params = {};
    if (selectedBatiment) params.batiment_id = selectedBatiment;
    if (selectedEtage) params.etage_id = selectedEtage;
    if (selectedBureau) params.bureau_id = selectedBureau;
    
    const token = localStorage.getItem('token');
    const url = inventoryApi.downloadReportPdf(id, params);
    
    // Open in new tab with authentication
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    
    // For authenticated download, we need to fetch with token
    fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => response.blob())
    .then(blob => {
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `rapport_inventaire_${report?.site_name || 'inventaire'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    })
    .catch(err => {
      console.error('Erreur téléchargement PDF:', err);
    });
  };

  const getEtatBadge = (etat) => {
    const colors = {
      'neuf': 'bg-green-100 text-green-800',
      'bon': 'bg-blue-100 text-blue-800',
      'usage': 'bg-yellow-100 text-yellow-800',
      'abime': 'bg-orange-100 text-orange-800',
      'hors_service': 'bg-red-100 text-red-800'
    };
    const labels = {
      'neuf': 'Neuf',
      'bon': 'Bon état',
      'usage': 'Usagé',
      'abime': 'Abîmé',
      'hors_service': 'Hors service'
    };
    return (
      <Badge className={colors[etat] || 'bg-gray-100 text-gray-800'}>
        {labels[etat] || etat}
      </Badge>
    );
  };

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
        <Button onClick={() => navigate('/inventory')} className="mt-4">
          Retour aux inventaires
        </Button>
      </div>
    );
  }

  const filteredMissing = filterItems(report?.missing_items || []);
  const filteredFound = filterItems(report?.found_items || []);

  return (
    <div className="space-y-6" data-testid="inventory-report-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/inventory')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Rapport d'inventaire</h1>
            <p className="text-gray-500">
              {report?.inventory_name || 'Inventaire'} - {report?.site_name}
              {report?.filter_location && ` > ${report.filter_location}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={handleExportPdf} 
            className="flex items-center gap-2"
            data-testid="export-pdf-btn"
          >
            <FileDown className="h-4 w-4" />
            Exporter PDF
          </Button>
          <Badge variant={report?.status === 'termine' ? 'default' : 'secondary'}>
            {report?.status === 'termine' ? 'Terminé' : 'En cours'}
          </Badge>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Package className="h-4 w-4" />
              Attendu
            </div>
            <p className="text-2xl font-bold mt-1">{report?.summary?.expected || 0}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-green-600 text-sm">
              <Check className="h-4 w-4" />
              Trouvés
            </div>
            <p className="text-2xl font-bold mt-1 text-green-600">{report?.summary?.found || 0}</p>
            <p className="text-xs text-gray-500">{report?.summary?.found_percentage || 0}%</p>
          </CardContent>
        </Card>
        
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-600 text-sm">
              <PackageX className="h-4 w-4" />
              Manquants
            </div>
            <p className="text-2xl font-bold mt-1 text-red-600">{report?.summary?.missing || 0}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-orange-600 text-sm">
              <AlertTriangle className="h-4 w-4" />
              Non référencés
            </div>
            <p className="text-2xl font-bold mt-1 text-orange-600">{report?.summary?.extra || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-600 text-sm">
              <Euro className="h-4 w-4" />
              Valeur manquante
            </div>
            <p className="text-2xl font-bold mt-1 text-red-600">
              {formatCurrency(report?.summary?.total_missing_value || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filtrer par localisation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Select value={selectedBatiment || undefined} onValueChange={(val) => setSelectedBatiment(val === 'all' ? '' : val)}>
              <SelectTrigger className="w-[200px]" data-testid="filter-batiment">
                <SelectValue placeholder="Tous les bâtiments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les bâtiments</SelectItem>
                {batiments.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.nom}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedEtage || undefined} onValueChange={(val) => setSelectedEtage(val === 'all' ? '' : val)} disabled={!selectedBatiment}>
              <SelectTrigger className="w-[200px]" data-testid="filter-etage">
                <SelectValue placeholder="Tous les étages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les étages</SelectItem>
                {etages.map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.nom}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedBureau || undefined} onValueChange={(val) => setSelectedBureau(val === 'all' ? '' : val)} disabled={!selectedEtage}>
              <SelectTrigger className="w-[200px]" data-testid="filter-bureau">
                <SelectValue placeholder="Tous les bureaux" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les bureaux</SelectItem>
                {bureaux.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.nom}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher par code, marque, modèle..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Missing by Location Summary */}
      {report?.missing_by_location && Object.keys(report.missing_by_location).length > 0 && (
        <Card className="border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-red-700">
              <MapPin className="h-4 w-4" />
              Manquants par localisation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(report.missing_by_location).map(([location, data]) => (
                <div key={location} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                  <span className="text-sm font-medium">{location}</span>
                  <div className="flex items-center gap-4">
                    <Badge variant="destructive">{data.count} manquant(s)</Badge>
                    {data.value > 0 && (
                      <span className="text-sm text-red-600 font-medium">
                        {formatCurrency(data.value)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs for Found/Missing lists */}
      <Tabs defaultValue="missing" className="w-full">
        <TabsList>
          <TabsTrigger value="missing" className="flex items-center gap-2">
            <PackageX className="h-4 w-4" />
            Manquants ({filteredMissing.length})
          </TabsTrigger>
          <TabsTrigger value="found" className="flex items-center gap-2">
            <Check className="h-4 w-4" />
            Trouvés ({filteredFound.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="missing">
          <Card>
            <CardContent className="p-0">
              {filteredMissing.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <PackageX className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p>Aucun mobilier manquant</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Code-barres</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Type</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Marque / Modèle</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Localisation</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">État</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600">Valeur</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredMissing.map((item, index) => (
                        <tr key={index} className="hover:bg-red-50" data-testid={`missing-item-${index}`}>
                          <td className="p-3">
                            <span className="font-mono text-sm">{item.code_barres}</span>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline">{item.type_label}</Badge>
                          </td>
                          <td className="p-3">
                            <div className="font-medium">{item.marque || '-'}</div>
                            <div className="text-sm text-gray-500">{item.modele || '-'}</div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1 text-sm">
                              <MapPin className="h-3 w-3 text-gray-400" />
                              {item.location_path}
                            </div>
                          </td>
                          <td className="p-3">
                            {getEtatBadge(item.etat)}
                          </td>
                          <td className="p-3 text-right font-medium">
                            {item.cout_achat ? formatCurrency(item.cout_achat) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="found">
          <Card>
            <CardContent className="p-0">
              {filteredFound.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-4" />
                  <p>Aucun mobilier trouvé</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Code-barres</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Type</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Marque / Modèle</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Localisation</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Scanné le</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Transfert</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredFound.map((item, index) => (
                        <tr key={index} className="hover:bg-green-50" data-testid={`found-item-${index}`}>
                          <td className="p-3">
                            <span className="font-mono text-sm">{item.code_barres}</span>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline">{item.type_label}</Badge>
                          </td>
                          <td className="p-3">
                            <div className="font-medium">{item.marque || '-'}</div>
                            <div className="text-sm text-gray-500">{item.modele || '-'}</div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1 text-sm">
                              <MapPin className="h-3 w-3 text-gray-400" />
                              {item.location_path}
                            </div>
                          </td>
                          <td className="p-3 text-sm text-gray-500">
                            {item.scanned_at ? new Date(item.scanned_at).toLocaleString('fr-FR') : '-'}
                          </td>
                          <td className="p-3">
                            {item.transfer_created && (
                              <Badge className="bg-blue-100 text-blue-800">Transféré</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InventoryReportPage;
